/**
 * External dependencies
 */
import React from 'react';
import { Switch, useLocation } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';

/**
 * Internal dependencies
 */
import { FadeSwitchProps } from './types';

// 🔴 修改點 1：我們把 class 名稱改成 .super-slow 避免被舊樣式干擾
const debugSlowStyles = {
  // 基礎設定
  '.super-slow-enter, .super-slow-exit': {
    willChange: 'opacity, transform',
    backfaceVisibility: 'hidden',
    perspective: '1000px'
  } as const,

  // --- 1. 進場 (新頁面) ---
  '.super-slow-enter': {
    opacity: 0,
    transform: 'translate3d(0, 40px, 0)',
    zIndex: 1
  },
  '.super-slow-enter.super-slow-enter-active': {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
    // 🔴 測試：設定成 1000ms (1秒)，這絕對會非常非常慢
    // 延遲 500ms 讓舊頁面先跑
    transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1) 500ms, transform 800ms cubic-bezier(0.4, 0, 0.2, 1) 500ms'
  },

  // --- 2. 退場 (舊頁面) ---
  '.super-slow-exit': {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
    zIndex: 0
  },
  '.super-slow-exit.super-slow-exit-active': {
    opacity: 0,
    transform: 'translate3d(0, -40px, 0)',
    // 🔴 測試：設定成 1000ms (2秒)
    transition: 'opacity 1000ms ease-in-out, transform 800ms ease-in-out'
  }
} as const;

const FadeSwitch: React.FC<FadeSwitchProps> = ({ children, location: externalLocation }) => {
  const internalLocation = useLocation();
  const currentLocation = externalLocation || internalLocation;

  return (
    <>
      {/* 注入新的樣式 */}
      <GlobalStyles styles={debugSlowStyles} />

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          touchAction: 'none'
        }}
      >
        <TransitionGroup component={null}>
          <CSSTransition
            key={currentLocation.key}
            // 🔴 修改點 2：Class 名稱要對應上面的設定
            classNames="super-slow"
            // 🔴 修改點 3：這是一定要改的關鍵！
            // Timeout 必須大於 CSS 的時間，否則動畫會被切斷。
            // 這裡設定 4000ms (4秒) 以確保包含所有延遲和動畫時間
            timeout={2000}
            unmountOnExit
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'background.default',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}
            >
              <Switch location={currentLocation}>{children}</Switch>
            </Box>
          </CSSTransition>
        </TransitionGroup>
      </Box>
    </>
  );
};

export default FadeSwitch;
